import { ChangeDetectorRef, Component, ChangeDetectionStrategy } from '@angular/core';
import { DateTime } from 'luxon';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { AuthService } from 'services/auth.service';
import { AuthRole, authRoleToStrings, authRoleFromStrings } from '@cf-shared/couch-auth';
import { describeAuthRoles } from 'util/formatting';
import { CsPage, NavController } from 'providers/nav-controller';
import { CouchUserInfo, UserLicenseType } from '@cf-shared/data-model/couch-user';
import { OfficerGrantAction, buildOfficerGrantKeyData, decideOfficerGrantAction, isRealKeyRedemption } from '@cf-shared/data-model/officer-grant';
import { reconcileLicenseState } from '@cf-shared/data-model/license-reconciler';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { AdminUserPermissionsPageParams } from './admin-user-permissions-interface';
import { untranslatedString } from 'models/translated-string';
import { describeErrorAndReportIfUnexpected } from 'util/version-mismatch-error-policy';

@Component({
    selector: 'page-admin-user-permissions',
    templateUrl: 'admin-user-permissions.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminUserPermissionsPage extends CsPage<AdminUserPermissionsPageParams> {
  protected email: string;
  protected role: AuthRole;
  protected originalRole: AuthRole;
  protected searching: boolean;
  protected userInfo: CouchUserInfo;
  protected error = '';
  // admin page: the role NAME comes through translated, while `adminExplanation` stays English
  // on purpose -- admin pages are the project's standing carve-out from translation.
  protected authRoleDescriptions = describeAuthRoles();
  protected AuthRole = AuthRole;
  protected loading = true;

  constructor(public navCtrl: NavController, private auth: AuthService, private cdRef: ChangeDetectorRef) {
    super();
  }

  // Reads the account. A `null` answer means the account genuinely does not
  // exist; a THROW means the lookup failed or was refused. The caller must keep
  // those two apart — see the catch below.
  async loadUserAsync(): Promise<CouchUserInfo> {
    return await this.auth.obtainCouchUserInfoForUserAsync(this.email);
  }

  async ionViewDidEnter() {
    this.email = this.navParams.data.email;
    try {
      this.loading = true;
      this.userInfo = await this.loadUserAsync();
      if (this.userInfo != null) {
        if (this.userInfo.name == this.auth.couch.user) {
          this.error = "You can't edit yourself";
        } else {
          this.error = '';
          this.role = authRoleFromStrings(this.userInfo.roles);
          this.originalRole = this.role;
        }
      } else {
        // only a genuinely absent account may say this
        this.error = 'User not found';
      }
    } catch (error) {
      // a failed or refused lookup is NOT "user not found" — that would be a false
      // statement about production data. shared policy: see the helper.
      console.error(`Admin user permissions failed for ${this.email}: `, error);
      this.error = describeErrorAndReportIfUnexpected(
        error,
        this.auth.logging,
        untranslatedString('Failed to load user permissions'),
      );
    } finally {
      this.loading = false;
      this.cdRef.markForCheck();
    }
  }

  hasChanges(): boolean {
    return this.role !== this.originalRole;
  }

  // Names, in plain words, the access record that is keeping this account's
  // role up. Read AFTER the recompute, so `licenseType` says which record won.
  // A redeemed activation key is only one of the possibilities - a live
  // subscription and a coach's cover do the same thing - and naming the wrong
  // one would send the officer off to clear something that is not there.
  private describeWhatKeepsTheAccess(): string {
    const licenseType = this.userInfo?.licenseType;
    if (!licenseType) return 'an access record on the account';
    if (licenseType === UserLicenseType.Key) {
      return isRealKeyRedemption(this.userInfo.keyData)
        ? 'a redeemed activation key'
        : 'an access grant made by hand';
    }
    if (licenseType === UserLicenseType.Coach) return 'access granted to it by its coach';
    return `a live "${licenseType}" subscription`;
  }

  handleCancelButtonClicked(event: Event) {
    lockClick(event, this.navCtrl.popAsync());
  }

  handleApplyButtonClicked(event: Event) {
    lockClick(event, this.handleApplyAsync());
  }

  async handleApplyAsync() {
    if (!this.userInfo || !this.hasChanges()) {
      return;
    }

    // The decision itself lives in the shared module, where it is unit-tested.
    // See `officer-grant.ts` for why granting `paid` or `vip` has to write a
    // stored record, why taking one back has to delete that record, and why an
    // account that already pays is refused.
    const now = DateTime.utc();
    const chosenRole = Number(this.role) as AuthRole;
    const action = decideOfficerGrantAction(chosenRole, this.userInfo, now);

    // native alerts to match the failure path below, which this screen has
    // always used
    if (action === OfficerGrantAction.RefuseRealRedemption) {
      alert(
        'This account has redeemed a real activation key, so granting access by hand '
        + 'would overwrite that record. Change the key instead, or ask a developer to '
        + 'clear the redemption first.',
      );
      return;
    }
    if (action === OfficerGrantAction.RefuseAlreadyPaying) {
      alert(
        'This account already has access it is paying for, so granting access by hand '
        + 'would hide the controls it uses to cancel or manage that subscription. '
        + 'Nothing was changed.',
      );
      return;
    }

    // Every outcome below decides the stored record outright rather than only
    // adding to it, so a failed write cannot leave a half-applied grant behind
    // on the in-memory document for a later apply to save.
    this.userInfo.roles = authRoleToStrings(chosenRole);
    if (action === OfficerGrantAction.GrantWithBlock) {
      this.userInfo.keyData = buildOfficerGrantKeyData(chosenRole, this.auth.couch.user, now);
    } else if (action === OfficerGrantAction.RemoveGrantBlock) {
      delete this.userInfo.keyData;
    }

    // Bring the account's summary fields back into agreement with the blocks
    // we just changed, the same way the 2026-05 goodwill-grant script does.
    // Those summary fields are what several server endpoints read WITHOUT
    // recomputing anything, so leaving them stale is not cosmetic: a grant
    // would be ignored by every one of them until something else happened to
    // recompute and save. Deleting a grant is the sharper case, because the
    // fields left behind would say the account still holds a never-expiring
    // licence that no longer has anything behind it.
    reconcileLicenseState(this.userInfo, now);

    // That recompute can disagree with what the officer picked, because the
    // role follows whatever access record the account still holds. A lowered
    // role does not stick while something on the account still justifies the
    // old one. Without saying so the screen would close on a revocation that
    // was never stored, and the officer would walk away believing it had been.
    const storedRole = authRoleFromStrings(this.userInfo.roles);
    const accessSource = this.describeWhatKeepsTheAccess();

    try {
      await this.auth.writeCouchUserInfoForUserAsync(this.userInfo);
      this.role = storedRole;
      this.originalRole = storedRole;
      this.cdRef.markForCheck();
      if (storedRole !== chosenRole) {
        const storedRoleName = this.authRoleDescriptions.find(d => d.value === storedRole)?.name ?? String(storedRole);
        alert(
          `Saved, but this account keeps the "${storedRoleName}" role, because it still has `
          + `${accessSource}. Remove or change that first to lower the role.`,
        );
      }
      await this.navCtrl.popAsync();
    } catch (error) {
      // #4283: the write now runs the client-side version guard too, so it can
      // refuse rather than only fail. Same shared policy as the load path above —
      // honest wording for the refusal, Sentry for anything unexpected — instead
      // of the console-only swallow this used to do.
      // native alert kept as-is — this page already used one here, and swapping in
      // the AlertController would be an unrelated change to an officer-only screen
      alert(String(describeErrorAndReportIfUnexpected(
        error,
        this.auth.logging,
        untranslatedString('User update failed! Please reload and try again.'),
      )));
    }
  }
}
